'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { linkFreshness } from '@/lib/drone-telemetry';
import type { AlarmEntry } from '@/components/dashboard/alarmTypes';
import type { LiveDrone } from '@/hooks/useWebSocket';

/** Enough to pick from at a glance; past this the answer is a better query, so
 *  the panel says how many were held back rather than silently dropping them. */
const MAX_RESULTS = 8;

type ResultState = 'alert' | 'flying' | 'idle' | 'offline';

/**
 * The state painted on a result row.
 *
 * Alert wins first, exactly as the strip board buckets it — an aircraft in
 * alarm must never read as a calm green FLY in one list while the list beside
 * it shows it red.
 *
 * After that, a lost link wins over the last status received: an aircraft that
 * has said nothing for 60 s is OFF, whatever it was doing when it went quiet.
 * NOTE this is deliberately stricter than `StripBoard.getSortBucket`, which
 * checks `flying` before age and so still paints a silent aircraft green. The
 * display must not vouch for a state nothing has confirmed in a minute.
 */
function resultState(
  drone: LiveDrone,
  alertDroneIds: Set<string>,
  now: number = Date.now(),
): ResultState {
  if (alertDroneIds.has(drone.drone_id)) return 'alert';
  if (linkFreshness(drone.last_seen_at, now) === 'lost') return 'offline';
  return drone.status === 'flying' ? 'flying' : 'idle';
}

const STATE_BADGE: Record<ResultState, { label: string; className: string }> = {
  alert: { label: 'ALERT', className: 'bg-status-red/10 text-status-red-fg' },
  flying: { label: 'FLY', className: 'bg-status-green/10 text-status-green-fg' },
  idle: { label: 'IDLE', className: 'bg-status-blue/10 text-status-blue-fg' },
  offline: { label: 'OFF', className: 'bg-status-gray/15 text-status-gray-fg' },
};

/** The callsign an operator sees everywhere else on the COP, with the same
 *  fallback the state card and video windows use for an unnamed airframe. */
function searchCallsign(drone: LiveDrone): string {
  return drone.callsign?.trim() || drone.drone_id.slice(-6).toUpperCase();
}

/**
 * Ranks every match for one query: what the operator started typing first,
 * then alphabetically.
 *
 * Both keys are INVARIANT UNDER TELEMETRY, and that is the point. Ranking by
 * live state instead (flying above idle above offline) reorders the panel at
 * the 1 Hz telemetry rate — an aircraft hovering either side of the 2 m mark
 * flips flying/idle every frame — so a row can change identity between the
 * operator reading it and pressing ENTER. On a tactical display that means
 * commanding the wrong aircraft. State is shown on the badge, never in the
 * order.
 */
export function rankDroneMatches(drones: LiveDrone[], query: string): LiveDrone[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const matches = drones.filter((d) => {
    const callsign = searchCallsign(d).toLowerCase();
    return callsign.includes(q) || d.drone_id.toLowerCase().includes(q);
  });

  matches.sort((a, b) => {
    const aCallsign = searchCallsign(a).toLowerCase();
    const bCallsign = searchCallsign(b).toLowerCase();
    const aPrefix = aCallsign.startsWith(q) || a.drone_id.toLowerCase().startsWith(q);
    const bPrefix = bCallsign.startsWith(q) || b.drone_id.toLowerCase().startsWith(q);
    if (aPrefix !== bPrefix) return aPrefix ? -1 : 1;
    return aCallsign.localeCompare(bCallsign);
  });

  return matches;
}

interface CopDroneSearchProps {
  /** The drones on the COP — already site-filtered by the page, so the search
   *  can never surface an aircraft the operator's filtered view excludes.
   *  This is the LIVE set: an airframe that has not transmitted on this
   *  connection is not in it, which is what the empty state says out loud. */
  drones: LiveDrone[];
  /** Open alarms, so a drone in alert reads red here as it does on the strip
   *  board rather than as a calm FLY. */
  alerts: AlarmEntry[];
  /** Selects the drone and flies the map to it (the page's handleFlyToDrone). */
  onSelect: (droneId: string) => void;
}

/** Header search: find an aircraft by callsign or ID and put the map on it. */
export function CopDroneSearch({ drones, alerts, onSelect }: CopDroneSearchProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // The highlight is anchored to an AIRCRAFT, never to a row number. A row
  // index survives its aircraft leaving the result set and would then commit
  // whichever drone slid into that slot.
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const alertDroneIds = useMemo(() => {
    const ids = new Set<string>();
    for (const alert of alerts) {
      if (alert.status !== 'resolved' && alert.drone_id) ids.add(alert.drone_id);
    }
    return ids;
  }, [alerts]);

  const ranked = useMemo(() => rankDroneMatches(drones, query), [drones, query]);
  const results = ranked.slice(0, MAX_RESULTS);
  const withheld = ranked.length - results.length;
  const showPanel = open && query.trim().length > 0;

  // Badges are derived from elapsed time, so they go stale on their own if
  // nothing re-renders — precisely when the link has dropped and the panel is
  // sitting open. Every other time-derived surface here ticks for this reason
  // (StripBoard, DroneSelectionChip, DroneCommandBar, DroneWorkspace).
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!showPanel) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [showPanel]);

  // No explicit highlight means the top row, which is what is painted as
  // selected. An explicit highlight whose aircraft has since left the results
  // resolves to -1: nothing is painted, and ENTER refuses rather than
  // retargeting to a neighbour.
  const activeIndex =
    highlightId === null
      ? (results.length > 0 ? 0 : -1)
      : results.findIndex((d) => d.drone_id === highlightId);

  const reset = () => {
    setQuery('');
    setOpen(false);
    setHighlightId(null);
  };

  /**
   * Selecting a result must NOT hand the keyboard back to the command bar.
   *
   * `DroneCommandBar` ignores keystrokes whose target is an INPUT, which is the
   * only thing keeping a callsign from being read as flight commands. Blurring
   * moved focus to `document.body` and lifted that guard while the box still
   * looked like a search box — so typing the next callsign sent `a` = ARM
   * (no confirm dialog: `arm` is not in CONFIRM_COMMANDS) to the aircraft that
   * had just been selected, then `l` = LAND. Focus stays here; the operator
   * leaves the field deliberately or not at all.
   */
  const commit = (droneId: string) => {
    onSelect(droneId);
    reset();
  };

  const moveHighlight = (delta: 1 | -1) => {
    if (results.length === 0) return;
    const next = activeIndex < 0 ? 0 : Math.min(Math.max(activeIndex + delta, 0), results.length - 1);
    setHighlightId(results[next].drone_id);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      // The COP listens for Escape on `window` to close the DETAILS workspace.
      // Dismissing this dropdown must not also tear down the panel behind it.
      e.stopPropagation();
      // Two-stage, per the ARIA combobox pattern: the first Escape dismisses
      // the list and keeps the text, the second clears the text. Focus never
      // moves on either — Escape is the universal "nothing happened" gesture,
      // so an operator who mistypes, escapes and keeps typing must not have
      // those keystrokes land on the command bar as ARM / LAND / GO-TO.
      if (open) setOpen(false);
      else if (query !== '') reset();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      moveHighlight(1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveHighlight(-1);
      return;
    }
    if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      commit(results[activeIndex].drone_id);
    }
  };

  const statusMessage =
    drones.length === 0
      ? 'No telemetry received yet'
      : results.length === 0
        ? `No match among ${drones.length} transmitting aircraft`
        : `${ranked.length} matching aircraft`;

  return (
    // `data-cop-text-scope` claims the keyboard for this whole subtree, not
    // just the input. DroneCommandBar's guard keys off tagName, which covers
    // the field but not the clear button — a normal tab stop one Tab past it,
    // where typing would have issued flight commands. The listbox rows are
    // inside too, so arrowing through results is never a command either.
    <div ref={rootRef} data-cop-text-scope className="relative hidden md:block w-44 lg:w-56 xl:w-64">
      <Search
        className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none"
        strokeWidth={1.5}
        aria-hidden
      />
      <Input
        ref={inputRef}
        type="text"
        role="combobox"
        // Only while the listbox exists — a dangling reference is worse than none.
        aria-expanded={showPanel}
        aria-controls={showPanel ? 'cop-drone-search-results' : undefined}
        aria-activedescendant={
          showPanel && activeIndex >= 0 ? `cop-drone-search-option-${activeIndex}` : undefined
        }
        aria-autocomplete="list"
        aria-label="Search drones"
        placeholder="Search drones..."
        autoComplete="off"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlightId(null);
        }}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          // Focus moving to the clear button is still inside the control; only
          // leaving it altogether closes the panel.
          if (e.relatedTarget && rootRef.current?.contains(e.relatedTarget as Node)) return;
          setOpen(false);
        }}
        onKeyDown={handleKeyDown}
        className="pl-8 pr-9 text-xs"
      />
      {query && (
        <button
          type="button"
          // preventDefault keeps focus in the input; the work happens on click
          // so the control also responds to ENTER/SPACE from the keyboard.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            reset();
            inputRef.current?.focus();
          }}
          aria-label="Clear drone search"
          className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="size-3.5" strokeWidth={1.5} />
        </button>
      )}

      <span className="sr-only" role="status" aria-live="polite">
        {showPanel ? statusMessage : ''}
      </span>

      {showPanel && (
        <div
          id="cop-drone-search-results"
          role="listbox"
          aria-label="Drone search results"
          className="absolute top-full left-0 mt-1 w-full z-50 surface-chrome rounded-lg py-1 overflow-hidden"
        >
          {results.length === 0 ? (
            // The live region above already speaks this; announcing it twice
            // is worse than not at all.
            <p
              aria-hidden
              className="px-3 py-2 text-[11px] uppercase tracking-wide text-muted-foreground"
            >
              {statusMessage}
            </p>
          ) : (
            <>
              {results.map((drone, i) => {
                const badge = STATE_BADGE[resultState(drone, alertDroneIds)];
                return (
                  <button
                    key={drone.drone_id}
                    type="button"
                    id={`cop-drone-search-option-${i}`}
                    role="option"
                    aria-selected={i === activeIndex}
                    // Focus stays on the input (aria-activedescendant pattern),
                    // so these must not be tab stops of their own.
                    tabIndex={-1}
                    // Keeps focus on the input so blur does not tear the panel
                    // down before the click resolves.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => commit(drone.drone_id)}
                    onMouseEnter={() => setHighlightId(drone.drone_id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                      i === activeIndex ? 'bg-accent/20' : 'hover:bg-accent/10'
                    }`}
                  >
                    <span className="callsign text-[13px] text-foreground truncate flex-1">
                      {searchCallsign(drone)}
                    </span>
                    <span
                      className={`shrink-0 px-1.5 py-0.5 rounded text-[11px] tracking-wide ${badge.className}`}
                    >
                      {badge.label}
                    </span>
                  </button>
                );
              })}
              {withheld > 0 && (
                <p className="px-3 py-1.5 border-t border-subtle text-[10px] uppercase tracking-wide text-muted-foreground">
                  {results.length} of {ranked.length} · refine search
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
